import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, IconButton,
  Alert, CircularProgress, Grid, Divider, Stack, Chip, useMediaQuery, useTheme
} from '@mui/material';
import {
  MessageCircle, CheckCircle, AlertCircle, Clock, Send, Bug,
  ChevronLeft, Info
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import API_BASE_URL from '../../Config/config';
import { TOKENS, PageContainer, PageHeader, SectionCard, SectionTitle, GradientButton } from '../components/NextGenPortalUI';

// --- Design Tokens ------------------------------------------------------------
const BRAND_PRIMARY = TOKENS.brand.primary;
const BRAND_GRADIENT = TOKENS.brand.gradient;
const BORDER = `1px solid ${TOKENS.border}`;
const BG_MAIN = TOKENS.bg.page;
const CARD_BG = TOKENS.bg.card;

const Star = ({ size }) => <span style={{ fontSize: size }}>⭐</span>;
const Settings = ({ size }) => <span style={{ fontSize: size }}>⚙️</span>;
const User = ({ size }) => <span style={{ fontSize: size }}>👤</span>;

const CATEGORIES = [
  { value: 'Bug', label: 'Report a Bug', icon: <Bug size={16} />, desc: 'Unexpected errors, broken features, or incorrect behaviour' },
  { value: 'Feedback', label: 'Feedback', icon: <MessageCircle size={16} />, desc: 'Suggestions, feature requests, or general platform feedback' },
  { value: 'Billing', label: 'Billing', icon: <AlertCircle size={16} />, desc: 'Credit purchase issues, invoice queries, or payment disputes' },
  { value: 'Feature Request', label: 'Feature Request', icon: <Star size={16} />, desc: 'Request new features or enhancements' },
  { value: 'Technical Issue', label: 'Technical Issue', icon: <Settings size={16} />, desc: 'Issues with tools, environment, or code execution' },
  { value: 'Account Issue', label: 'Account Issue', icon: <User size={16} />, desc: 'Login, profile, or permission related issues' },
  { value: 'Other', label: 'Other Query', icon: <Send size={16} />, desc: "Anything else that doesn't fit the categories above" },
];

const RESPONSE_TIMES = [
  { icon: <Bug size={14} />, label: 'Bug Reports', time: 'Within 24 hrs' },
  { icon: <AlertCircle size={14} />, label: 'Billing Issues', time: 'Within 12 hrs' },
  { icon: <MessageCircle size={14} />, label: 'Feedback', time: 'Within 48 hrs' },
  { icon: <Send size={14} />, label: 'General Queries', time: 'Within 48 hrs' },
];

const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'pdf', 'doc', 'docx', 'txt', 'zip'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SupportNextGen = () => {
  const { nextgen_builder } = useNextGenAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('Bug');
  const [success, setSuccess] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (nextgen_builder) {
      setEmail(nextgen_builder.emailAddress || nextgen_builder.email || '');
    }
  }, [nextgen_builder]);


  const getSystemInfo = () => {
    const ua = navigator.userAgent;
    let browser = "Unknown";
    if (ua.indexOf("Firefox") > -1) browser = "Firefox";
    else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
    else if (ua.indexOf("Safari") > -1) browser = "Safari";
    else if (ua.indexOf("Edge") > -1) browser = "Edge";

    let os = "Unknown";
    if (ua.indexOf("Win") > -1) os = "Windows";
    else if (ua.indexOf("Mac") > -1) os = "MacOS";
    else if (ua.indexOf("Linux") > -1) os = "Linux";
    else if (ua.indexOf("Android") > -1) os = "Android";
    else if (ua.indexOf("like Mac") > -1) os = "iOS";

    return {
      browser,
      os,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      appVersion: "1.0.0" // Example version
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');
    setSuccess(false);

    if (!email || !message.trim() || !category || !subject.trim()) {
      setFieldError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const sysInfo = getSystemInfo();
      const fd = new FormData();
      fd.append('user_email', email);
      fd.append('category', category);
      fd.append('subject', subject);
      fd.append('message', message);
      fd.append('user_type', 'builder');
      fd.append('application', 'NEXTGEN_BUILDER');
      fd.append('user_id', nextgen_builder?.id || '');
      fd.append('submitted_by', nextgen_builder?.fullName || '');
      fd.append('browser', sysInfo.browser);
      fd.append('os', sysInfo.os);
      fd.append('timezone', sysInfo.timezone);
      fd.append('app_version', sysInfo.appVersion);
      
      const res = await axios.post(`${API_BASE_URL}/api/support`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status === 201) {
        setSuccess(true);
        setTicketId(res.data.ticket?.id || '');
        // Reset form
        setMessage('');
        setSubject('');
        setCategory('Bug');
      }
    } catch (err) {
      console.error('Error submitting ticket:', err);
      const serverMsg = err.response?.data?.error;
      setFieldError(serverMsg || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: TOKENS.bg.page, minHeight: 'calc(100vh - 64px)' }}>
      <PageContainer>
        <PageHeader
          icon={<MessageCircle size={24} />}
          eyebrow="Support"
          title="Support Center"
          description="Submit a ticket and our team will get back to you shortly."
        />

        <Grid container spacing={3}>
          {/* Left: Info & Guidelines */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Categories */}
              <SectionCard padding={2.5}>
                <SectionTitle title="How can we help?" />
                <Stack spacing={1}>
                  {CATEGORIES.map((cat) => {
                    const isSelected = category === cat.value;
                    return (
                      <Box
                        key={cat.value}
                        onClick={() => { setCategory(cat.value); setSuccess(false); }}
                        sx={{
                          p: 1.5, borderRadius: TOKENS.radius.control, cursor: 'pointer',
                          border: isSelected ? `1.5px solid ${BRAND_PRIMARY}` : BORDER,
                          bgcolor: isSelected ? `${BRAND_PRIMARY}0F` : TOKENS.bg.subtle,
                          boxShadow: isSelected ? `0 0 0 3px ${BRAND_PRIMARY}14` : 'none',
                          transition: 'all 0.2s',
                          display: 'flex', gap: 1.5,
                          '&:hover': { borderColor: isSelected ? BRAND_PRIMARY : '#D1D5DB' },
                        }}
                      >
                        <Box
                          sx={{
                            width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: isSelected ? '#FFFFFF' : TOKENS.text.muted,
                            background: isSelected ? BRAND_GRADIENT : 'rgba(107,114,128,0.08)',
                          }}
                        >
                          {cat.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isSelected ? BRAND_PRIMARY : TOKENS.text.primary, fontSize: '0.85rem' }}>
                            {cat.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: TOKENS.text.secondary, display: 'block', lineHeight: 1.35 }}>
                            {cat.desc}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </SectionCard>

              {/* Response Times */}
              <SectionCard padding={2.5}>
                <SectionTitle icon={<Clock size={16} />} title="Response Times" />
                <Stack spacing={1.5}>
                  {RESPONSE_TIMES.map((item) => (
                    <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: TOKENS.text.muted }}>{item.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: TOKENS.text.secondary, fontSize: '0.8rem' }}>{item.label}</Typography>
                      </Box>
                      <Chip label={item.time} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: `${BRAND_PRIMARY}10`, color: BRAND_PRIMARY, border: `1px solid ${BRAND_PRIMARY}22` }} />
                    </Box>
                  ))}
                </Stack>
              </SectionCard>

              {/* Faster Resolution Tip */}
              <Box
                sx={{
                  p: 2.25, borderRadius: TOKENS.radius.card,
                  background: 'linear-gradient(135deg, rgba(108,59,255,0.08), rgba(139,92,246,0.05))',
                  border: `1px solid ${BRAND_PRIMARY}22`,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: BRAND_PRIMARY, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Info size={16} /> Faster Resolution
                </Typography>
                <Typography variant="caption" sx={{ color: TOKENS.text.caption, lineHeight: 1.6, fontWeight: 500 }}>
                  Include the specific page or feature name, steps to reproduce, and any error messages.
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Right: Support Form */}
          <Grid item xs={12} md={8}>
            <SectionCard padding={{ xs: 2.5, md: 4 }} sx={{ height: '100%' }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                  Submit a Support Ticket
                </Typography>
                <Typography variant="body2" sx={{ color: TOKENS.text.secondary, fontWeight: 500 }}>
                  We'll respond to your registered email address within 24–48 business hours.
                </Typography>
              </Box>

              {success && (
                <motion.div initial={{ opacity: 0, scale: 0.95, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <Alert
                    severity="success"
                    icon={false}
                    sx={{ mb: 3, borderRadius: '14px', border: '1px solid #BBF7D0', bgcolor: '#F0FDF4', py: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: '#16A34A', color: '#FFFFFF',
                        }}
                      >
                        <CheckCircle size={18} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#14532D' }}>
                          Support request submitted successfully!
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#15803D' }}>
                          Your ticket number is <b>#{ticketId}</b>. Our support team will review your request and respond to your registered email address.
                        </Typography>
                      </Box>
                    </Box>
                  </Alert>
                </motion.div>
              )}

              {fieldError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                  {fieldError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {/* Email (Read-only) */}
                  <TextField
                    label="Registered Email"
                    value={email}
                    disabled
                    fullWidth
                    variant="outlined"
                    size="small"
                    helperText="Replies will be sent to this email address"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: TOKENS.bg.subtle, borderRadius: TOKENS.radius.control } }}
                  />

                  {/* Subject */}
                  <TextField
                    label="Subject"
                    placeholder="Summary of the issue"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setSuccess(false); }}
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: TOKENS.radius.control } }}
                  />

                  {/* Category Dropdown */}
                  <TextField
                    select
                    label="Issue Category"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setSuccess(false); }}
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: TOKENS.radius.control } }}
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Description */}
                  <TextField
                    label="Description"
                    placeholder="Please provide details about your issue..."
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); setSuccess(false); }}
                    required
                    multiline
                    rows={6}
                    fullWidth
                    variant="outlined"
                    helperText={`${message.length} characters`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: TOKENS.radius.control } }}
                  />


                  <GradientButton
                    type="submit"
                    disabled={submitting || !subject.trim() || !message.trim()}
                    endIcon={null}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send size={18} />}
                    sx={{ py: 1.5, fontSize: '0.95rem' }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Support Request'}
                  </GradientButton>

                  <Typography variant="caption" sx={{ textAlign: 'center', color: TOKENS.text.muted, fontWeight: 500 }}>
                    By submitting, you agree to our support terms. Tickets are reviewed Mon–Sat, 9 am–6 pm IST.
                  </Typography>
                </Stack>
              </form>
            </SectionCard>
          </Grid>
        </Grid>
      </PageContainer>
    </Box>
  );
};

export default SupportNextGen;
